import React, { useEffect, useMemo, useRef, useState } from "react";

export type PromptPackReviewPrompt = {
  sort_order: number;
  category: string;
  prompt_type: "organic" | "guided";
  prompt_weight: number;
  prompt_text: string;
  prompt_intent?: string | null;
  prompt_intent_detail?: string | null;
};

export type PromptPackReviewDraftPrompt = {
  sort_order: unknown;
  category: unknown;
  prompt_type: unknown;
  prompt_weight: unknown;
  prompt_text: unknown;
  prompt_intent?: unknown;
  prompt_intent_detail?: unknown;
  [key: string]: unknown;
};

export type PromptPackReviewDraftPack = {
  label: string;
  summary?: string | null;
  product_type?: string | null;
  pack_key?: string | null;
  prompt_count: number;
  prompts: PromptPackReviewDraftPrompt[];
  warnings?: string[];
  validation_summary?: Record<string, unknown> | null;
  counts_by_category?: Record<string, number> | null;
  prompt_mix_notes?: string | null;
  prompt_diversity?: unknown;
  desired_prompt_count?: number;
  category_key?: string | null;
  draft_id?: string | null;
  draft_status?: string | null;
  queued_at?: string | null;
  completed_at?: string | null;
  warning_count?: number | null;
  generation_governance_summary?: Record<string, unknown> | null;
  [key: string]: unknown;
};

export type PromptPackReviewFilters = {
  category: string;
  promptType: "all" | "organic" | "guided";
  search: string;
};

export type PromptPackReviewGroup = {
  category: string;
  prompts: PromptPackReviewPrompt[];
};

export type PromptPackReviewSummary = {
  totalPrompts: number;
  organicCount: number;
  guidedCount: number;
  categoryCounts: Array<{ category: string; count: number }>;
  validationSummary: Record<string, unknown> | null;
  warnings: string[];
};

export type PromptPackReviewIntegrityIssue = {
  code: string;
  message: string;
  details?: string[];
};

export type PromptPackReviewValidationResult = {
  ok: boolean;
  errors: PromptPackReviewIntegrityIssue[];
  summary: PromptPackReviewSummary;
  promptCount: number;
  normalizedPrompts: PromptPackReviewPrompt[];
  metadataCategoryCounts: Array<{ category: string; count: number }> | null;
  metadataValidationSummary: Record<string, unknown> | null;
  safeSummary: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeSearchTerm(value: string) {
  return value.trim().toLowerCase();
}

function normalizePromptToken(value: string) {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

const STRICT_NUMERIC_PATTERN = /^-?\d+(?:\.\d+)?$/;

function buildIssue(code: string, message: string, details?: string[]): PromptPackReviewIntegrityIssue {
  return details?.length ? { code, message, details } : { code, message };
}

export function normalizePromptWeight(value: unknown): number | null {
  if (isFiniteNumber(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || !STRICT_NUMERIC_PATTERN.test(trimmed)) {
    return null;
  }

  const normalized = Number(trimmed);
  return Number.isFinite(normalized) ? normalized : null;
}

function extractCategoryCounts(value: unknown) {
  if (!value || typeof value !== "object") return null;

  if (Array.isArray(value)) {
    const entries = value
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const category = Reflect.get(entry, "category");
        const count = Reflect.get(entry, "count");
        if (!isNonBlankString(category)) return null;
        return {
          category: category.trim(),
          count: Number(count || 0)
        };
      })
      .filter((entry): entry is { category: string; count: number } => Boolean(entry));

    return entries.length ? entries : null;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .map(([category, count]) => ({
      category,
      count: Number(count || 0)
    }))
    .filter((entry) => entry.category.trim().length > 0);

  return entries.length ? entries : null;
}

function sameCategoryCounts(
  left: Array<{ category: string; count: number }>,
  right: Array<{ category: string; count: number }>
) {
  if (left.length !== right.length) return false;

  const leftMap = new Map(left.map((entry) => [entry.category.trim().toLowerCase(), entry.count]));
  return right.every((entry) => {
    const key = entry.category.trim().toLowerCase();
    return leftMap.has(key) && leftMap.get(key) === entry.count;
  });
}

function extractSummaryCount(summary: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = summary[key];
    if (isFiniteNumber(value)) return value;
  }
  return null;
}

function extractSummaryCategoryCounts(summary: Record<string, unknown>) {
  return extractCategoryCounts(summary.category_counts ?? summary.counts_by_category ?? summary.categoryCounts);
}

export function validatePromptPackReviewDraft(pack: PromptPackReviewDraftPack): PromptPackReviewValidationResult {
  const errors: PromptPackReviewIntegrityIssue[] = [];
  const rawPrompts = Array.isArray(pack.prompts) ? pack.prompts : [];
  const normalizedPrompts: PromptPackReviewPrompt[] = [];
  const derivedCategoryCounts = new Map<string, number>();
  const seenSortOrders = new Set<number>();
  const seenNormalizedPromptKeys = new Set<string>();
  const sortOrders: number[] = [];
  let organicCount = 0;
  let guidedCount = 0;

  if (!Array.isArray(pack.prompts)) {
    errors.push(buildIssue("prompt_array_invalid", "prompts must be an array."));
  }

  if (!rawPrompts.length) {
    errors.push(buildIssue("prompt_array_empty", "prompts must not be empty."));
  }

  rawPrompts.forEach((prompt, index) => {
    if (!prompt || typeof prompt !== "object") {
      errors.push(buildIssue("prompt_row_invalid", `Prompt row ${index + 1} is not a valid object.`));
      return;
    }

    const row = prompt as Partial<PromptPackReviewPrompt>;
    const sortOrder = row.sort_order;
    const category = row.category;
    const promptText = row.prompt_text;
    const promptType = row.prompt_type;
    const promptWeight = normalizePromptWeight(row.prompt_weight);
    const intent = row.prompt_intent;
    const intentDetail = row.prompt_intent_detail;

    if (!Number.isInteger(sortOrder) || !isFiniteNumber(sortOrder) || sortOrder <= 0) {
      errors.push(buildIssue("prompt_sort_order_invalid", `Prompt row ${index + 1} has an invalid sort order.`));
    } else {
      if (seenSortOrders.has(sortOrder)) {
        errors.push(buildIssue("prompt_sort_order_duplicate", `Prompt sort order ${sortOrder} appears more than once.`));
      }
      seenSortOrders.add(sortOrder);
      sortOrders.push(sortOrder);
    }

    if (!isNonBlankString(category)) {
      errors.push(buildIssue("prompt_category_missing", `Prompt row ${index + 1} is missing a category.`));
    }

    if (!isNonBlankString(promptText)) {
      errors.push(buildIssue("prompt_text_missing", `Prompt row ${index + 1} is missing prompt text.`));
    }

    if (promptType !== "organic" && promptType !== "guided") {
      errors.push(buildIssue("prompt_type_invalid", `Prompt row ${index + 1} has an invalid prompt type.`));
    }

    if (promptWeight === null) {
      errors.push(buildIssue("prompt_weight_invalid", `Prompt row ${index + 1} has an invalid prompt weight.`));
    }

    if (intent !== null && intent !== undefined && typeof intent !== "string") {
      errors.push(buildIssue("prompt_intent_invalid", `Prompt row ${index + 1} has a non-string prompt intent.`));
    }

    if (intentDetail !== null && intentDetail !== undefined && typeof intentDetail !== "string") {
      errors.push(buildIssue("prompt_intent_detail_invalid", `Prompt row ${index + 1} has a non-string prompt intent detail.`));
    }

    if (
      isNonBlankString(category) &&
      isNonBlankString(promptText) &&
      (promptType === "organic" || promptType === "guided") &&
      promptWeight !== null &&
      Number.isInteger(sortOrder) &&
      sortOrder > 0
    ) {
      const normalizedCategory = normalizePromptToken(category);
      const normalizedPromptText = normalizePromptToken(promptText);
      const normalizedKey = `${normalizedCategory}::${promptType}::${normalizedPromptText}`;

      if (promptType === "organic" && promptWeight !== 1) {
        errors.push(buildIssue("organic_prompt_weight_invalid", `Prompt row ${index + 1} has an invalid organic prompt weight.`));
      } else if (promptType === "guided" && promptWeight !== 0.1) {
        errors.push(buildIssue("guided_prompt_weight_invalid", `Prompt row ${index + 1} has an invalid guided prompt weight.`));
      }

      if (seenNormalizedPromptKeys.has(normalizedKey)) {
        errors.push(buildIssue("duplicate_prompt_text", `Duplicate prompt text detected for category "${category}" and type "${promptType}".`));
      } else {
        seenNormalizedPromptKeys.add(normalizedKey);
      }

      derivedCategoryCounts.set(category.trim(), (derivedCategoryCounts.get(category.trim()) || 0) + 1);
      if (promptType === "guided") {
        guidedCount += 1;
      } else {
        organicCount += 1;
      }

      normalizedPrompts.push({
        sort_order: sortOrder,
        category: category.trim(),
        prompt_type: promptType,
        prompt_weight: promptWeight,
        prompt_text: promptText,
        prompt_intent: typeof intent === "string" ? intent : intent ?? null,
        prompt_intent_detail: typeof intentDetail === "string" ? intentDetail : intentDetail ?? null
      });
    }
  });

  const promptCount = rawPrompts.length;
  const derivedSortOrders = sortOrders.slice().sort((left, right) => left - right);
  const derivedCategoryCountEntries = Array.from(derivedCategoryCounts.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((left, right) => left.category.localeCompare(right.category));
  const metadataCategoryCounts = extractCategoryCounts(pack.counts_by_category);
  const validationSummary = isRecord(pack.validation_summary) ? pack.validation_summary : null;

  if (pack.prompt_count !== promptCount) {
    errors.push(buildIssue("prompt_count_mismatch", `prompt_count ${pack.prompt_count} does not match the number of persisted prompts (${promptCount}).`));
  }

  if (derivedSortOrders.length !== promptCount || derivedSortOrders.some((value, index) => value !== index + 1)) {
    errors.push(buildIssue("prompt_sort_sequence_invalid", "Prompt sort orders must form a complete sequence from 1 through the prompt count."));
  }

  if (organicCount + guidedCount !== promptCount) {
    errors.push(buildIssue("prompt_mix_count_mismatch", "Organic and guided prompt totals do not add up to the full prompt count."));
  }

  if (derivedCategoryCountEntries.reduce((total, entry) => total + entry.count, 0) !== promptCount) {
    errors.push(buildIssue("category_count_total_mismatch", "Category counts do not add up to the full prompt count."));
  }

  if (metadataCategoryCounts && !sameCategoryCounts(metadataCategoryCounts, derivedCategoryCountEntries)) {
    errors.push(buildIssue("metadata_category_counts_mismatch", "Metadata category counts do not match the persisted prompts."));
  }

  if (validationSummary) {
    const summaryCount = extractSummaryCount(validationSummary, ["prompt_count", "total_prompts", "total_prompt_count"]);
    if (summaryCount !== null && summaryCount !== promptCount) {
      errors.push(buildIssue("validation_summary_prompt_count_mismatch", "Validation summary prompt count contradicts the persisted prompts."));
    }

    const summaryOrganic = extractSummaryCount(validationSummary, ["organic_count"]);
    if (summaryOrganic !== null && summaryOrganic !== organicCount) {
      errors.push(buildIssue("validation_summary_organic_count_mismatch", "Validation summary organic count contradicts the persisted prompts."));
    }

    const summaryGuided = extractSummaryCount(validationSummary, ["guided_count"]);
    if (summaryGuided !== null && summaryGuided !== guidedCount) {
      errors.push(buildIssue("validation_summary_guided_count_mismatch", "Validation summary guided count contradicts the persisted prompts."));
    }

    const summaryCategoryCounts = extractSummaryCategoryCounts(validationSummary);
    if (summaryCategoryCounts && !sameCategoryCounts(summaryCategoryCounts, derivedCategoryCountEntries)) {
      errors.push(buildIssue("validation_summary_category_counts_mismatch", "Validation summary category counts contradict the persisted prompts."));
    }

    const promptMix = validationSummary.prompt_mix;
    if (promptMix && typeof promptMix === "object") {
      const promptMixOrganic = extractSummaryCount(promptMix as Record<string, unknown>, ["organic", "organic_count"]);
      const promptMixGuided = extractSummaryCount(promptMix as Record<string, unknown>, ["guided", "guided_count"]);
      if (
        (promptMixOrganic !== null && promptMixOrganic !== organicCount) ||
        (promptMixGuided !== null && promptMixGuided !== guidedCount)
      ) {
        errors.push(buildIssue("validation_summary_prompt_mix_mismatch", "Validation summary prompt mix contradicts the persisted prompts."));
      }
    }
  }

  const sortedPrompts = normalizedPrompts.slice().sort((left, right) => left.sort_order - right.sort_order);

  const summary: PromptPackReviewSummary = {
    totalPrompts: promptCount,
    organicCount,
    guidedCount,
    categoryCounts: derivedCategoryCountEntries,
    validationSummary,
    warnings: Array.isArray(pack.warnings)
      ? pack.warnings.filter((warning): warning is string => typeof warning === "string")
      : []
  };

  return {
    ok: errors.length === 0,
    errors,
    summary,
    promptCount,
    normalizedPrompts: sortedPrompts,
    metadataCategoryCounts,
    metadataValidationSummary: validationSummary,
    safeSummary: errors.length
      ? `Integrity validation failed with ${errors.length} issue${errors.length === 1 ? "" : "s"}.`
      : `Validated ${promptCount} prompts across ${derivedCategoryCountEntries.length} categories.`
  };
}

export function buildPromptPackReviewSummary(pack: PromptPackReviewDraftPack): PromptPackReviewSummary {
  return validatePromptPackReviewDraft(pack).summary;
}

export function formatPromptPackReviewIntegrityError(result: PromptPackReviewValidationResult) {
  const firstIssue = result.errors[0];
  const extraIssues = result.errors.slice(1, 4).map((issue) => `${issue.code}: ${issue.message}`);

  return {
    code: "prompt_pack_review_integrity_failed",
    message: firstIssue ? `${result.safeSummary} First issue: ${firstIssue.message}` : result.safeSummary,
    details: extraIssues.length ? extraIssues : undefined
  };
}

export function filterPromptPackReviewPrompts(
  prompts: PromptPackReviewPrompt[],
  filters: PromptPackReviewFilters
) {
  const search = normalizeSearchTerm(filters.search);

  return prompts.filter((prompt) => {
    const categoryMatch =
      filters.category === "all" ||
      String(prompt.category || "Uncategorized") === filters.category;
    const promptTypeMatch =
      filters.promptType === "all" || prompt.prompt_type === filters.promptType;

    if (!categoryMatch || !promptTypeMatch) {
      return false;
    }

    if (!search) {
      return true;
    }

    const haystack = [
      prompt.sort_order,
      prompt.category,
      prompt.prompt_type,
      prompt.prompt_weight,
      prompt.prompt_text,
      prompt.prompt_intent,
      prompt.prompt_intent_detail
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(search);
  });
}

export function groupPromptPackReviewPrompts(prompts: PromptPackReviewPrompt[]): PromptPackReviewGroup[] {
  const groups = new Map<string, PromptPackReviewPrompt[]>();
  const order: string[] = [];

  for (const prompt of prompts) {
    const category = String(prompt.category || "Uncategorized");
    if (!groups.has(category)) {
      groups.set(category, []);
      order.push(category);
    }
    groups.get(category)!.push(prompt);
  }

  return order.map((category) => ({
    category,
    prompts: groups.get(category) || []
  }));
}

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(", ");

function getFocusableElements(container: HTMLElement | null) {
  if (!container) return [];

  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute("disabled")
  );
}

type PromptPackReviewDialogProps = {
  isOpen: boolean;
  draftPromptPack: PromptPackReviewDraftPack | null;
  canSave: boolean;
  hasBeenApplied: boolean;
  isSaving: boolean;
  saveStatus: string;
  onClose: () => void;
  onSave: (draftPromptPack: PromptPackReviewDraftPack) => void;
};

function cloneReviewDraftPack(pack: PromptPackReviewDraftPack): PromptPackReviewDraftPack {
  return {
    ...pack,
    prompts: Array.isArray(pack.prompts) ? pack.prompts.map((prompt) => ({ ...prompt })) : []
  };
}

function getPromptWarningEntries(
  validationSummary: Record<string, unknown> | null,
  prompt: PromptPackReviewPrompt,
  index: number
) {
  if (!validationSummary) return [];
  const warningEntries: Array<Record<string, unknown>> = [];
  const governance = isRecord(validationSummary.governance) ? validationSummary.governance : null;
  const governanceDiagnostics = governance && isRecord(governance.diagnostics) ? governance.diagnostics : null;
  const sources = [
    governanceDiagnostics?.customer_competitor_mentions,
    governanceDiagnostics?.prohibited_recommendation_mentions,
    isRecord(validationSummary.language_quality) ? validationSummary.language_quality.invalid : null,
    isRecord(validationSummary.diagnostics) ? validationSummary.diagnostics.samples : null
  ];

  sources.forEach((source) => {
    if (!Array.isArray(source)) return;
    source.forEach((entry) => {
      if (!isRecord(entry)) return;
      const entryIndex = typeof entry.index === "number" ? entry.index : null;
      const entrySortOrder = typeof entry.sort_order === "number" ? entry.sort_order : null;
      if (entryIndex === index || entrySortOrder === prompt.sort_order) warningEntries.push(entry);
    });
  });

  return warningEntries;
}

export function PromptPackReviewDialog({
  isOpen,
  draftPromptPack,
  canSave,
  hasBeenApplied,
  isSaving,
  saveStatus,
  onClose,
  onSave
}: PromptPackReviewDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [workingDraftPromptPack, setWorkingDraftPromptPack] = useState<PromptPackReviewDraftPack | null>(
    () => (draftPromptPack ? cloneReviewDraftPack(draftPromptPack) : null)
  );
  const [editingSortOrder, setEditingSortOrder] = useState<number | null>(null);
  const [editValues, setEditValues] = useState({ prompt_text: "", prompt_intent: "", prompt_intent_detail: "" });
  const [editError, setEditError] = useState("");
  const [editedSortOrders, setEditedSortOrders] = useState<Set<number>>(new Set());

  useEffect(() => {
    setWorkingDraftPromptPack(draftPromptPack ? cloneReviewDraftPack(draftPromptPack) : null);
    setEditingSortOrder(null);
    setEditError("");
    setEditedSortOrders(new Set());
  }, [draftPromptPack, isOpen]);
  const [filters, setFilters] = useState<PromptPackReviewFilters>({
    category: "all",
    promptType: "all",
    search: ""
  });

  const validation = useMemo(
    () => (workingDraftPromptPack ? validatePromptPackReviewDraft(workingDraftPromptPack) : null),
    [workingDraftPromptPack]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFirstElement = () => {
      const focusableElements = getFocusableElements(dialogRef.current);
      const target = focusableElements[0] || dialogRef.current;
      target?.focus();
    };

    const rafId = window.requestAnimationFrame(focusFirstElement);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isSaving) {
          return;
        }
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = getFocusableElements(dialogRef.current);
      if (!focusableElements.length) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(rafId);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      if (openerRef.current && openerRef.current.isConnected) {
        openerRef.current.focus();
      }
    };
  }, [isOpen, onClose, isSaving]);

  useEffect(() => {
    if (!isOpen) {
      setFilters({
        category: "all",
        promptType: "all",
        search: ""
      });
    }
  }, [isOpen]);

  const visiblePrompts = useMemo(() => {
    if (!validation?.ok || !workingDraftPromptPack) return [];
    return filterPromptPackReviewPrompts(validation.normalizedPrompts, filters);
  }, [filters, validation, workingDraftPromptPack]);

  const groupedPrompts = useMemo(() => groupPromptPackReviewPrompts(visiblePrompts), [visiblePrompts]);

  const categoryOptions = useMemo(() => {
    if (!validation?.ok) return [];

    return ["all", ...validation.summary.categoryCounts.map((entry) => entry.category)];
  }, [validation]);

  if (!isOpen || !workingDraftPromptPack || !validation) {
    return null;
  }

  const hasEdits = editedSortOrders.size > 0;
  const canActuallySave =
    canSave &&
    validation.ok &&
    (!hasBeenApplied || hasEdits);
  const validationErrors = validation.errors;
  const promptCountLabel =
    visiblePrompts.length === validation.summary.totalPrompts
      ? `Showing all ${validation.summary.totalPrompts} prompts`
      : `Showing ${visiblePrompts.length} of ${validation.summary.totalPrompts} prompts`;

  const setAllFilters = () => {
    setFilters({
      category: "all",
      promptType: "all",
      search: ""
    });
  };

  const startEditingPrompt = (prompt: PromptPackReviewPrompt) => {
    setEditingSortOrder(prompt.sort_order);
    setEditValues({
      prompt_text: prompt.prompt_text,
      prompt_intent: prompt.prompt_intent || "",
      prompt_intent_detail: prompt.prompt_intent_detail || ""
    });
    setEditError("");
  };

  const cancelEditingPrompt = () => {
    setEditingSortOrder(null);
    setEditError("");
  };

  const saveEditingPrompt = () => {
    if (!editValues.prompt_text.trim()) {
      setEditError("Prompt text cannot be empty.");
      return;
    }
    if (!editValues.prompt_intent.trim()) {
      setEditError("Prompt intent cannot be empty.");
      return;
    }

    setWorkingDraftPromptPack((current) => {
      if (!current) return current;
      return {
        ...current,
        prompts: current.prompts.map((prompt) =>
          Number(prompt.sort_order) === editingSortOrder
            ? {
                ...prompt,
                prompt_text: editValues.prompt_text,
                prompt_intent: editValues.prompt_intent,
                prompt_intent_detail: editValues.prompt_intent_detail
              }
            : prompt
        )
      };
    });
    if (editingSortOrder !== null) {
      setEditedSortOrders((current) => new Set(current).add(editingSortOrder));
    }
    setEditingSortOrder(null);
    setEditError("");
  };

  return (
    <div
      role="presentation"
      onClick={() => {
        if (!isSaving) {
          onClose();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.72)",
        backdropFilter: "blur(6px)",
        zIndex: 1200,
        padding: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-pack-review-title"
        aria-describedby="prompt-pack-review-description"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(1280px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 32px)",
          background: "#f8fafc",
          borderRadius: 20,
          boxShadow: "0 30px 70px rgba(15, 23, 42, 0.35)",
          border: "1px solid rgba(148, 163, 184, 0.35)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        <div style={{
          padding: "20px 22px 16px",
          borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div>
              <div id="prompt-pack-review-title" style={{ color: "#0f172a", fontSize: 20, fontWeight: 950 }}>
                Review Draft Prompt Pack
              </div>
              <div id="prompt-pack-review-description" style={{ color: "#475569", fontSize: 13, marginTop: 6, lineHeight: 1.45 }}>
                {workingDraftPromptPack.label} · {validation.summary.totalPrompts} persisted prompts · Review before saving and applying.
              </div>
              <div style={{ marginTop: 8, color: "#1d4ed8", fontSize: 12, fontWeight: 900 }}>
                Showing latest generated draft
              </div>
              <div data-testid="prompt-draft-metadata" style={{ marginTop: 6, color: "#64748b", fontSize: 11, lineHeight: 1.6 }}>
                {workingDraftPromptPack.draft_id ? `Draft ID: ${workingDraftPromptPack.draft_id}` : null}
                {workingDraftPromptPack.draft_status ? ` · Status: ${workingDraftPromptPack.draft_status}` : null}
                {workingDraftPromptPack.completed_at || workingDraftPromptPack.queued_at
                  ? ` · ${workingDraftPromptPack.completed_at ? "Completed" : "Queued"}: ${new Date(String(workingDraftPromptPack.completed_at || workingDraftPromptPack.queued_at)).toLocaleString()}`
                  : null}
                {` · Prompts: ${validation.summary.totalPrompts}`}
                {` · Warnings: ${workingDraftPromptPack.warning_count ?? validation.summary.warnings.length}`}
                {isRecord(workingDraftPromptPack.generation_governance_summary) &&
                workingDraftPromptPack.generation_governance_summary.brand_guidance_field_count !== undefined
                  ? ` · Brand Guidance fields: ${String(workingDraftPromptPack.generation_governance_summary.brand_guidance_field_count)}`
                  : null}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              style={{
                border: "1px solid #cbd5e1",
                background: isSaving ? "#e2e8f0" : "#fff",
                color: isSaving ? "#64748b" : "#0f172a",
                borderRadius: 999,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 900,
                cursor: isSaving ? "not-allowed" : "pointer",
                opacity: isSaving ? 0.7 : 1
              }}
            >
              Close
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 16 }}>
            <InfoChip label="Total" value={validation.summary.totalPrompts} />
            <InfoChip label="Organic" value={validation.summary.organicCount} />
            <InfoChip label="Guided" value={validation.summary.guidedCount} />
            {validation.summary.categoryCounts.map((entry) => (
              <InfoChip key={entry.category} label={entry.category} value={entry.count} />
            ))}
          </div>

          {workingDraftPromptPack.summary ? (
            <p style={{ margin: "14px 0 0", color: "#334155", fontSize: 13, lineHeight: 1.6 }}>
              {workingDraftPromptPack.summary}
            </p>
          ) : null}
        </div>

        <div style={{ padding: 18, borderBottom: "1px solid rgba(148, 163, 184, 0.18)", background: "#fff" }}>
          {validationErrors.length ? (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 14, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 13, lineHeight: 1.5, fontWeight: 700 }}>
              <div style={{ fontWeight: 950 }}>{formatPromptPackReviewIntegrityError(validation).code}</div>
              <div style={{ marginTop: 4 }}>{validation.safeSummary}</div>
              <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                {validationErrors.slice(0, 5).map((issue) => (
                  <li key={issue.code}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {validation.summary.warnings.length ? (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 14, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 13, lineHeight: 1.5, fontWeight: 700 }}>
              Warnings: {validation.summary.warnings.join(" · ")}
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, alignItems: "end" }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ color: "#334155", fontSize: 12, fontWeight: 900 }}>Category filter</span>
              <select
                aria-label="Category filter"
                value={filters.category}
                onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value }))}
                style={filterControlStyle}
              >
                <option value="all">All categories</option>
                {categoryOptions
                  .filter((option): option is string => option !== "all")
                  .map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ color: "#334155", fontSize: 12, fontWeight: 900 }}>Prompt type</span>
              <select
                aria-label="Prompt type filter"
                value={filters.promptType}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    promptType: event.target.value as PromptPackReviewFilters["promptType"]
                  }))
                }
                style={filterControlStyle}
              >
                <option value="all">All types</option>
                <option value="organic">Organic</option>
                <option value="guided">Guided</option>
              </select>
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ color: "#334155", fontSize: 12, fontWeight: 900 }}>Text search</span>
              <input
                aria-label="Prompt text search"
                type="search"
                value={filters.search}
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                placeholder="Search prompt text, intent, or category"
                style={filterControlStyle}
              />
            </label>

            <button
              type="button"
              onClick={setAllFilters}
              style={{
                alignSelf: "end",
                border: "1px solid #93c5fd",
                background: "#dbeafe",
                color: "#1d4ed8",
                borderRadius: 999,
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 900,
                cursor: "pointer",
                height: 44
              }}
            >
              Show all
            </button>
          </div>

          <div data-testid="prompt-count-label" style={{ marginTop: 10, color: "#475569", fontSize: 12, fontWeight: 800 }}>
            {promptCountLabel}
          </div>
        </div>

        <div style={{ padding: 18, overflowY: "auto", flex: "1 1 auto", background: "#f8fafc" }}>
          <div style={{ display: "grid", gap: 16 }}>
            <section style={{ padding: 16, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
              <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 900, marginBottom: 10 }}>
                Validation summary
              </div>
              <pre style={preStyle}>
                {JSON.stringify(validation.summary.validationSummary, null, 2)}
              </pre>
            </section>

            <section style={{ padding: 16, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
              <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 900, marginBottom: 10 }}>
                All persisted prompts
              </div>

              {groupedPrompts.length ? (
                <div style={{ display: "grid", gap: 18 }}>
                  {groupedPrompts.map((group) => (
                    <div key={group.category} data-testid="prompt-group" style={{ display: "grid", gap: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ color: "#1e293b", fontSize: 13, fontWeight: 900 }}>
                          {group.category}
                        </div>
                        <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                          {group.prompts.length} prompt{group.prompts.length === 1 ? "" : "s"}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 10 }}>
                        {group.prompts.map((prompt) => {
                          const isEditing = editingSortOrder === prompt.sort_order;
                          const promptWarnings = getPromptWarningEntries(
                            validation.summary.validationSummary,
                            prompt,
                            validation.normalizedPrompts.findIndex((entry) => entry.sort_order === prompt.sort_order)
                          );
                          return (
                          <div
                            data-testid="prompt-card"
                            key={`${group.category}-${prompt.sort_order}`}
                            style={{
                              border: "1px solid #dbeafe",
                              background: "#f8fbff",
                              borderRadius: 14,
                              padding: 14
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                              <div style={{ color: "#1e3a8a", fontSize: 12, fontWeight: 900, letterSpacing: 0.2 }}>
                                #{prompt.sort_order}
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                <SmallPill label={prompt.category} />
                                <SmallPill label={prompt.prompt_type} />
                                <SmallPill label={`weight ${prompt.prompt_weight}`} />
                                {editedSortOrders.has(prompt.sort_order) ? <SmallPill label="Edited" /> : null}
                                {promptWarnings.length ? <SmallPill label={`Warning${promptWarnings.length > 1 ? "s" : ""} ${promptWarnings.length}`} /> : null}
                              </div>
                            </div>

                            {isEditing ? (
                              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                                <label style={{ display: "grid", gap: 4, color: "#334155", fontSize: 12, fontWeight: 800 }}>
                                  Prompt text
                                  <textarea
                                    aria-label={`Prompt text #${prompt.sort_order}`}
                                    value={editValues.prompt_text}
                                    onChange={(event) => setEditValues((current) => ({ ...current, prompt_text: event.target.value }))}
                                    rows={3}
                                    style={editFieldStyle}
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4, color: "#334155", fontSize: 12, fontWeight: 800 }}>
                                  Prompt intent
                                  <input
                                    aria-label={`Prompt intent #${prompt.sort_order}`}
                                    value={editValues.prompt_intent}
                                    onChange={(event) => setEditValues((current) => ({ ...current, prompt_intent: event.target.value }))}
                                    style={editFieldStyle}
                                  />
                                </label>
                                <label style={{ display: "grid", gap: 4, color: "#334155", fontSize: 12, fontWeight: 800 }}>
                                  Prompt intent detail
                                  <textarea
                                    aria-label={`Prompt intent detail #${prompt.sort_order}`}
                                    value={editValues.prompt_intent_detail}
                                    onChange={(event) => setEditValues((current) => ({ ...current, prompt_intent_detail: event.target.value }))}
                                    rows={2}
                                    style={editFieldStyle}
                                  />
                                </label>
                                {editError ? <div role="alert" style={{ color: "#b91c1c", fontSize: 12, fontWeight: 800 }}>{editError}</div> : null}
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button type="button" onClick={saveEditingPrompt} style={secondaryButtonStyle}>Save</button>
                                  <button type="button" onClick={cancelEditingPrompt} style={mutedButtonStyle}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ color: "#0f172a", fontSize: 14, lineHeight: 1.55, marginTop: 10, whiteSpace: "pre-wrap" }}>
                                  {prompt.prompt_text}
                                </div>
                                <div style={{ marginTop: 10, display: "grid", gap: 8, color: "#334155", fontSize: 12, lineHeight: 1.5 }}>
                                  <div><strong>Prompt intent:</strong> {prompt.prompt_intent || "—"}</div>
                                  <div><strong>Prompt intent detail:</strong> {prompt.prompt_intent_detail || "—"}</div>
                                </div>
                                <div style={{ marginTop: 10 }}>
                                  <button type="button" aria-label={`Edit prompt #${prompt.sort_order}`} onClick={() => startEditingPrompt(prompt)} style={secondaryButtonStyle}>Edit</button>
                                </div>
                              </>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
                  No prompts match the active filters.
                </div>
              )}
            </section>
          </div>
        </div>

        <div style={{ padding: 18, borderTop: "1px solid rgba(148, 163, 184, 0.18)", background: "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ color: "#475569", fontSize: 12, lineHeight: 1.6, maxWidth: 760 }}>
              {hasBeenApplied
                ? hasEdits
                  ? "Saving creates a new account prompt-pack version, updates the persisted draft, and reapplies the edited prompts. The prior version remains available for history and rollback."
                  : "This prompt pack is already applied. Edit at least one prompt to create and apply a new version."
                : "Saving stores this draft as an account prompt pack and applies it to the account, archiving the existing active prompts."}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => onSave(workingDraftPromptPack)}
                disabled={!canActuallySave || isSaving}
                style={{
                  border: "1px solid #86efac",
                  background: !canActuallySave || isSaving ? "#dcfce7" : "#bbf7d0",
                  color: "#166534",
                  borderRadius: 999,
                  padding: "10px 16px",
                  fontSize: 13,
                  fontWeight: 900,
                  cursor: !canActuallySave || isSaving ? "not-allowed" : "pointer",
                  opacity: !canActuallySave || isSaving ? 0.65 : 1
                }}
              >
                {isSaving
                  ? "Saving..."
                  : hasBeenApplied
                    ? hasEdits
                      ? "Save edits and reapply"
                      : "Applied — edit a prompt to save again"
                    : "Save and apply as account prompt pack"}
              </button>
            </div>
          </div>

          {saveStatus ? (
            <div style={{ marginTop: 10, color: saveStatus.toLowerCase().includes("failed") ? "#b91c1c" : "#166534", fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>
              {saveStatus}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const editFieldStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #93c5fd",
  borderRadius: 8,
  padding: "8px 10px",
  color: "#0f172a",
  background: "#fff",
  font: "inherit",
  resize: "vertical"
};

const secondaryButtonStyle: React.CSSProperties = {
  border: "1px solid #93c5fd",
  background: "#dbeafe",
  color: "#1d4ed8",
  borderRadius: 999,
  padding: "7px 12px",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer"
};

const mutedButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  borderColor: "#cbd5e1",
  background: "#f8fafc",
  color: "#475569"
};

function InfoChip({ label, value }: { label: string; value: number }) {
  return (
    <div style={{
      padding: "10px 12px",
      borderRadius: 14,
      background: "#fff",
      border: "1px solid #e2e8f0",
      display: "grid",
      gap: 3
    }}>
      <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 950 }}>
        {value}
      </div>
    </div>
  );
}

function SmallPill({ label }: { label: string }) {
  return (
    <span style={{
      padding: "4px 8px",
      borderRadius: 999,
      background: "#dbeafe",
      color: "#1d4ed8",
      fontSize: 11,
      fontWeight: 900,
      textTransform: "none"
    }}>
      {label}
    </span>
  );
}

const filterControlStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 13,
  background: "#fff",
  color: "#0f172a",
  minHeight: 42
};

const preStyle: React.CSSProperties = {
  margin: 0,
  background: "#0f172a",
  color: "#e2e8f0",
  borderRadius: 12,
  padding: 14,
  fontSize: 12,
  lineHeight: 1.5,
  overflowX: "auto",
  whiteSpace: "pre-wrap"
};
